<script>
  import {createEventDispatcher, onMount} from 'svelte';
  import {fade} from 'svelte/transition';
  import {createPopper} from '@popperjs/core';

  /**
   * ------- placement support value -----
   * 'auto'
   * 'auto-start'
   * 'auto-end'
   * 'top'
   * 'top-start'
   * 'top-end'
   * 'bottom'
   * 'bottom-start'
   * 'bottom-end'
   * 'right'
   * 'right-start'
   * 'right-end'
   * 'left'
   * 'left-start'
   * 'left-end';
   */
  export let content = '';
  export let anchorNode = null;
  export let placement = 'top';
  export let isFade = true;
  export let background = '#000';
  export let zIndex = 99999;
  export let color = '#fff';
  export let border = '0';
  export let boxShadow = '';
  export let arrowBoxShadow = '';
  export let right = '';
  export let arrowRight = '';
  export let show = false;
  export let visible = false;
  export let style = {};
  export let hideDelay = 0;
  export let offset = [0, 8];
  export let noArrow = false;
  export let trigger = 'hover'; // click

  const dispatch = createEventDispatcher();

  let scrollWidth = 1200;
  let tooltipNode;
  let hover = false;

  $: createTip(tooltipNode, anchorNode);

  onMount(() => {
    if (trigger === 'click') {
      document.addEventListener('click', clickOutsideFun);
    }

    return function () {
      if (trigger === 'click') {
        document.removeEventListener('click', clickOutsideFun);
      }
    };
  });

  function createTip(tooltipNode, anchorNode) {
    if (!tooltipNode || !anchorNode) {
      return;
    }
    createPopper(anchorNode, tooltipNode, {
      placement,
      modifiers: [
        {
          name: 'offset',
          options: {offset},
        },
      ],
    });
  }

  function handleMouseEnter() {
    hover = true;
  }

  function handleMouseLeave() {
    hover = false;
  }

  function handleCloseClick() {
    hover = false;
    dispatch('clickClose');
  }

  function calcStyle(style, background, color, right, zIndex, border, boxShadow) {
    const styleStr = Object.entries(style)
      .map((item) => `${item[0]}:${item[1]}`)
      .join(';');
    return `${styleStr};background:${background};color:${color};right:${right};z-index:${zIndex};border:${border};${boxShadow};`;
  }

  function clickOutsideFun(e) {
    if (!(show || visible || hover)) return;
    if (anchorNode && anchorNode.contains(e.target)) return;

    if (!tooltipNode.contains(e.target)) {
      dispatch('clickOutside');
    }
  }
</script>

<style>
  .tooltip {
    border-radius: 8px;
    padding: 8px 10px 10px 8px;
    max-width: 450px;
    line-height: 20px;
    font-size: 14px;
    background: #000;
    color: #fff;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.1);
  }
  .tooltip.phone {
    max-width: 300px;
  }
  .arrow,
  .arrow2 {
    position: absolute;
    width: 8px;
    height: 8px;
  }
  .arrow2 {
    background: #000;
    transform: rotate(45deg);
    content: '';
  }
  .tooltip[data-popper-placement^='top'] > .arrow {
    bottom: -4px;
  }
  .tooltip[data-popper-placement^='bottom'] > .arrow {
    top: -4px;
  }
  .tooltip[data-popper-placement^='left'] > .arrow {
    right: -4px;
  }
  .tooltip[data-popper-placement^='right'] > .arrow {
    left: -4px;
  }
  .tooltip[data-popper-placement^='bottom'] {
    position: absolute;
    margin-top: 10px;
  }
  .tooltip[data-popper-placement^='top'] {
    position: absolute;
    display: flex;
    justify-content: center;
    margin-top: 10px;
    border-radius: 8px;
  }
  .icon {
    position: absolute;
    top: 5px;
    right: 5px;
    line-height: 12px;
  }
  .tooltip.p16 {
    padding: 16px;
  }
</style>

<svelte:options accessors={true} />

<svelte:window bind:innerWidth={scrollWidth} />

{#if show || visible || (trigger === 'hover' && hover)}
  <div
    class="tooltip"
    class:phone={scrollWidth < 640}
    class:p16={visible}
    transition:fade={isFade ? {} : {delay: 0, duration: 0}}
    bind:this={tooltipNode}
    data-popper-placement={placement}
    role="tooltip"
    style={calcStyle(style, background, color, right, zIndex, border, boxShadow)}
    on:mouseenter={handleMouseEnter}
    on:mouseover={handleMouseEnter}
    on:mouseleave={handleMouseLeave}
  >
    {#if visible}
      <div class="icon cp">
        <i class="iconfont lh12 f12 cp" style="color: {color}" on:click={handleCloseClick}>&#xe614;</i>
      </div>
    {/if}
    {@html content}
    <slot name="content" />
    {#if !noArrow}
      <div class="arrow" data-popper-arrow style="right:{arrowRight};">
        <div class="arrow2" style="background:{background};{arrowBoxShadow}" />
      </div>
    {/if}
  </div>
{/if}
